'use client';

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  THEME_COOKIE,
  THEME_STORAGE_KEY,
  isTheme,
  resolveTheme,
  type ResolvedTheme,
  type Theme,
} from '@/lib/theme/constants';

interface ThemeContextValue {
  /** Preferencia explícita del usuario: 'auto' | 'dark' | 'light' */
  theme: Theme;
  /** Tema efectivo aplicado al DOM: 'dark' | 'light' */
  resolvedTheme: ResolvedTheme;
  /** Cambia la preferencia (persiste en cookie + localStorage + DB si logueado). */
  setTheme: (theme: Theme) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

interface ThemeProviderProps {
  initialTheme: Theme;
  /** Se llama al cambiar tema, para persistir en DB cuando el usuario está logueado. */
  onPersist?: (theme: Theme) => void;
  children: React.ReactNode;
}

/**
 * Provider del tema NQORO. El script inline en `<head>` ya seteó `data-theme`
 * en `<html>` antes del primer paint; este provider hidrata el state React y
 * sincroniza cambios subsecuentes con DOM + cookie + localStorage + DB.
 */
export function ThemeProvider({ initialTheme, onPersist, children }: ThemeProviderProps) {
  const [theme, setThemeState] = useState<Theme>(initialTheme);
  const [resolvedTheme, setResolvedTheme] = useState<ResolvedTheme>(() => {
    if (typeof window === 'undefined') return resolveTheme(initialTheme);
    const attr = document.documentElement.getAttribute('data-theme');
    return attr === 'light' || attr === 'dark' ? attr : resolveTheme(initialTheme);
  });

  // Sincroniza `data-theme` y `data-theme-pref` con el state.
  useEffect(() => {
    const prefersDark =
      typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches;
    const next = resolveTheme(theme, prefersDark);
    setResolvedTheme(next);
    document.documentElement.setAttribute('data-theme', next);
    document.documentElement.setAttribute('data-theme-pref', theme);
  }, [theme]);

  // Si la preferencia es 'auto', escucha cambios en prefers-color-scheme y
  // re-resuelve para que el tema siga al sistema en tiempo real.
  useEffect(() => {
    if (theme !== 'auto' || typeof window === 'undefined') return;
    const mql = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = () => {
      const next: ResolvedTheme = mql.matches ? 'dark' : 'light';
      setResolvedTheme(next);
      document.documentElement.setAttribute('data-theme', next);
    };
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, [theme]);

  const setTheme = useCallback(
    (next: Theme) => {
      if (!isTheme(next)) return;
      setThemeState(next);
      try {
        document.cookie = `${THEME_COOKIE}=${encodeURIComponent(next)};path=/;max-age=${60 * 60 * 24 * 365};SameSite=Lax`;
        localStorage.setItem(THEME_STORAGE_KEY, next);
      } catch {
        // Cookies/storage bloqueados — no bloqueante.
      }
      onPersist?.(next);
    },
    [onPersist]
  );

  const value = useMemo<ThemeContextValue>(
    () => ({ theme, resolvedTheme, setTheme }),
    [theme, resolvedTheme, setTheme]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    // Fallback seguro fuera del provider: no hace nada, devuelve dark.
    return {
      theme: 'auto',
      resolvedTheme: 'dark',
      setTheme: () => {},
    };
  }
  return ctx;
}
